import { useEffect, useState } from 'react';
import { apiGet, apiJson } from '../api/client';

interface ApiKeyStatus {
  configured: boolean;
  updatedAt: number | null;
}

function formatTs(unixSeconds: number | null): string {
  if (!unixSeconds) return '—';
  return new Date(unixSeconds * 1000).toLocaleString();
}

interface KeyBlockProps {
  heading: string;
  subtitle: string;
  placeholder: string;
  statusPath: string;
  setPath: string;
  deletePath: string;
}

function KeyBlock({
  heading,
  subtitle,
  placeholder,
  statusPath,
  setPath,
  deletePath,
}: KeyBlockProps) {
  const [status, setStatus] = useState<ApiKeyStatus | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const refresh = async () => {
    try {
      const s = await apiGet<ApiKeyStatus>(statusPath);
      setStatus(s);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusPath]);

  const onSave = async () => {
    setErr(null);
    setBusy(true);
    try {
      await apiJson('PUT', setPath, { apiKey: draft.trim() });
      setDraft('');
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setErr(null);
    setBusy(true);
    try {
      await apiJson('DELETE', deletePath);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel">
      <h2>{heading}</h2>
      <p className="muted">{subtitle}</p>
      <div className="stack">
        <div>
          Configured:{' '}
          {status === null ? (
            <span className="muted">loading…</span>
          ) : status.configured ? (
            <span className="status-ok">yes</span>
          ) : (
            <span className="status-missing">no</span>
          )}
          {status?.configured && (
            <>
              {' '}
              <span className="muted">
                (updated {formatTs(status.updatedAt)})
              </span>
            </>
          )}
        </div>
        <div className="row">
          <input
            type="password"
            placeholder={placeholder}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            autoComplete="off"
          />
          <button
            className="btn-primary"
            onClick={onSave}
            disabled={busy || !draft.trim()}
          >
            Save
          </button>
          {status?.configured && (
            <button
              className="btn-danger"
              onClick={onDelete}
              disabled={busy}
            >
              Delete
            </button>
          )}
        </div>
        {err && <div className="status-missing">Error: {err}</div>}
      </div>
    </div>
  );
}

export function ApiKeysPanel() {
  return (
    <div className="stack">
      <KeyBlock
        heading="Gemini API key"
        subtitle="Stored in the Worker's D1 admin_settings table. Never returned to the client."
        placeholder="AI... (paste Gemini API key)"
        statusPath="/api/settings/apikey"
        setPath="/api/settings/apikey"
        deletePath="/api/settings/apikey"
      />
      <KeyBlock
        heading="OpenRouter API key"
        subtitle="Required for the kling-video-tool and future OpenRouter-backed media tools. Get a key at openrouter.ai/keys. Stored in the Worker's D1 admin_settings table; never returned to the client."
        placeholder="sk-or-v1-... (paste OpenRouter API key)"
        statusPath="/api/settings/openrouter-apikey"
        setPath="/api/settings/openrouter-apikey"
        deletePath="/api/settings/openrouter-apikey"
      />
    </div>
  );
}
